import type { WidgetNodeProps } from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import { NodeResizer, useReactFlow } from "@xyflow/react";
import { AnimatePresence } from "motion/react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CANVAS_SNAP } from "../canvas-controls.config";
import { useCanvasApi } from "../context/CanvasContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useSectionPreviewHighlight } from "../hooks/useSectionPreviewHighlight";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";
import { WidgetFloatingToolbar } from "./WidgetFloatingToolbar";
import { WIDGET_ALL_PRESET_MAP } from "./widgets";

const [SNAP_X, SNAP_Y] = CANVAS_SNAP.grid;
const snapTo = (v: number, step: number) => Math.round(v / step) * step;

/**
 * Memoized content wrapper — isolates the dangerouslySetInnerHTML div from
 * parent re-renders caused by selection / drag state changes. This is the
 * second memo layer (WidgetNode itself is the first). Together they ensure
 * CSS animations inside widget HTML are never interrupted by React
 * reconciliation unless the HTML actually changes.
 */
const WidgetContent = memo(function WidgetContent({ html }: { html: string }) {
  return (
    <div
      style={{
        filter: "drop-shadow(0 2px 12px rgba(0,0,0,0.10))",
        pointerEvents: "none",
        transformOrigin: "top left",
      }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

/**
 * Shell for React-component widgets.
 *
 * Reads the preset from WIDGET_ALL_PRESET_MAP to wire up:
 *   - NodeResizer with aspect-ratio lock and isResizingRef gating (when preset.resizable is set)
 *   - CSS scale wrapper so resizable component widgets fill the node bounds
 *   - WidgetFloatingToolbar with preset.toolbarSlot for extra actions
 *
 * Individual widget content components receive only their own concerns —
 * they never import node infrastructure.
 */
const ReactWidgetShell = memo(function ReactWidgetShell({
  id,
  data,
  selected,
  dragging,
  width,
  height,
  positionAbsoluteX,
  positionAbsoluteY,
  children,
}: WidgetNodeProps & { children: React.ReactNode }) {
  const canvas = useCanvasApi();
  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();
  const { canEdit } = canvas;
  const { updateNode } = useReactFlow();
  const { isResizingRef } = useCanvasUi();
  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  const preset = WIDGET_ALL_PRESET_MAP.get(String(data.presetId ?? ""));
  const resizeCfg = preset?.resizable;

  const resizeOriginRef = useRef<{ x: number; y: number } | null>(null);

  const defaultW = preset?.defaultWidth ?? 300;
  const defaultH = preset?.defaultHeight ?? 200;
  // scaleContent: false → fill mode (e.g. RetroBoardWidget uses w-full/h-full).
  // scaleContent: true (default) → CSS scale mode (Timer, Music).
  const scaleContent = resizeCfg?.scaleContent !== false;

  // ── CSS-scale mode: measure actual content size on first render ───────────
  //
  // Component widgets (Timer, Music) have a fixed intrinsic design size, but
  // preset.defaultHeight may not match the actual rendered height exactly.
  // We measure the DOM once and use the real size as the intrinsic reference.
  // Skipped for fill-mode widgets (RetroBoardWidget) since they reflow to fit.
  const contentRef = useRef<HTMLDivElement>(null);
  const [intrinsicSize, setIntrinsicSize] = useState<{
    w: number;
    h: number;
  } | null>(null);
  const shellSyncedRef = useRef(false);

  useEffect(() => {
    if (!resizeCfg || !scaleContent || shellSyncedRef.current) return;
    let rafId: number;
    const measure = () => {
      const el = contentRef.current;
      if (!el) return;
      const { offsetWidth, offsetHeight } = el;
      if (offsetWidth > 0 && offsetHeight > 0) {
        setIntrinsicSize({ w: offsetWidth, h: offsetHeight });
      } else {
        rafId = requestAnimationFrame(measure);
      }
    };
    rafId = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync Loro once measured dimensions are known.
  useEffect(() => {
    if (!intrinsicSize || shellSyncedRef.current) return;
    shellSyncedRef.current = true;

    let finalW = intrinsicSize.w;
    let finalH = intrinsicSize.h;

    if (width || height) {
      // Drag-placed: correct to measured aspect ratio.
      const dragW = width || intrinsicSize.w;
      const dragH = height || intrinsicSize.h;
      const s = Math.min(dragW / intrinsicSize.w, dragH / intrinsicSize.h);
      finalW = Math.round(intrinsicSize.w * s);
      finalH = Math.round(intrinsicSize.h * s);
    }

    if (
      Math.abs(finalW - (width || defaultW)) > 1 ||
      Math.abs(finalH - (height || defaultH)) > 1
    ) {
      updateNode(id, { width: finalW, height: finalH });
      canvas.updateNodeDimensions(id, { width: finalW, height: finalH });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intrinsicSize]);

  // ── Dimensions for rendering ──────────────────────────────────────────────
  // Fill mode: always use the node's Loro dimensions so w-full/h-full works.
  // Scale mode: use measured intrinsic size; fall back to preset defaults.
  const intrinsicW = intrinsicSize?.w ?? defaultW;
  const intrinsicH = intrinsicSize?.h ?? defaultH;
  const nodeW = width || (scaleContent ? intrinsicW : defaultW);
  const nodeH = height || (scaleContent ? intrinsicH : defaultH);

  // Scale mode: CSS scale to fill the node boundary.
  // Fill mode: scale is always 1 (content fills via w-full/h-full).
  const scale =
    scaleContent && resizeCfg
      ? Math.min(nodeW / intrinsicW, nodeH / intrinsicH)
      : 1;
  // Fill-mode: always scaled (handles + w-full/h-full need explicit dims).
  // Scale-mode: defer scaled state until intrinsic size is measured so the
  // component can render at its natural height first. Without this, the outer
  // div is clamped to preset.defaultHeight before the actual component height
  // is measured, clipping the content and blocking the measurement ref.
  const isScaled =
    resizeCfg != null &&
    nodeW > 0 &&
    nodeH > 0 &&
    (!scaleContent || intrinsicSize !== null);

  return (
    <>
      {resizeCfg && canEdit && (
        <NodeResizer
          isVisible={!!selected && !dragging}
          minWidth={resizeCfg.minWidth}
          maxWidth={resizeCfg.maxWidth}
          minHeight={resizeCfg.minHeight}
          keepAspectRatio={scaleContent}
          onResizeStart={(_evt, params) => {
            resizeOriginRef.current = { x: params.x, y: params.y };
            isResizingRef.current = true;
          }}
          onResizeEnd={(_evt, params) => {
            isResizingRef.current = false;
            const minW = resizeCfg.minWidth ?? 48;
            const minH = resizeCfg.minHeight ?? 48;
            // Snap width to grid. For scale-mode widgets derive height exactly
            // from the intrinsic ratio so handles never drift from visual content.
            // For fill-mode (retro board) snap both axes independently.
            const w = Math.max(minW, snapTo(params.width, SNAP_X));
            const h =
              scaleContent && intrinsicSize
                ? Math.max(
                    minH,
                    Math.round(intrinsicSize.h * (w / intrinsicSize.w)),
                  )
                : Math.max(minH, snapTo(params.height, SNAP_Y));
            const origin = resizeOriginRef.current;
            const x =
              origin && params.x !== origin.x
                ? params.x + params.width - w
                : params.x;
            const y =
              origin && params.y !== origin.y
                ? params.y + params.height - h
                : params.y;
            updateNode(id, { position: { x, y }, width: w, height: h });
            canvas.updateNodeDimensions(id, { width: w, height: h });
            canvas.updateNodePosition(id, { x, y });
          }}
        />
      )}

      <AnimatePresence>
        {selected &&
          selectedNodeCount === 1 &&
          !isMultiSelectToolbarOpen &&
          canEdit &&
          preset?.nodeProps?.deletable !== false && (
            <CanvasFloatingToolbarPortal
              positionAbsoluteX={positionAbsoluteX}
              positionAbsoluteY={positionAbsoluteY}
              width={nodeW}
              topOffset={56}
            >
              <WidgetFloatingToolbar
                toolbarSlot={preset?.toolbarSlot}
                nodeId={id}
                nodeData={data}
              />
            </CanvasFloatingToolbarPortal>
          )}
      </AnimatePresence>

      {/* Outer container — fills node dimensions when scaled */}
      <div
        className={cn(
          isSectionPreviewMember &&
            "ring-2 ring-canvas-selection/60 rounded-lg",
        )}
        style={
          isScaled
            ? {
                width: nodeW,
                height: nodeH,
                overflow: "hidden",
                position: "relative",
                // Smooth height transitions for fill-mode widgets (e.g. retro
                // board expanding as cards are added). Scale-mode widgets don't
                // benefit since scale changes are near-instant.
                transition: !scaleContent ? "height 0.25s ease" : undefined,
              }
            : { width: "fit-content", height: "fit-content" }
        }
      >
        {/* Scale wrapper — only applied in CSS-scale mode (Timer, Music).
            Fill-mode widgets (RetroBoardWidget) render directly via w-full/h-full.
            contentRef measures actual height before first Loro sync. */}
        <div
          ref={!isScaled || !scaleContent ? contentRef : undefined}
          style={
            isScaled && scaleContent
              ? {
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  width: intrinsicW,
                  height: intrinsicH,
                }
              : isScaled
                ? { width: "100%", height: "100%" }
                : undefined
          }
        >
          {children}
        </div>
      </div>
    </>
  );
});

/**
 * Data-driven routing wrapper.
 *
 * If the preset declares a `component`, that component is rendered inside
 * ReactWidgetShell (which handles toolbar, NodeResizer, CSS scale, etc.).
 * Otherwise the legacy HTML / CSS-scale path (WidgetNodeImpl) is used.
 *
 * Adding a new React widget never requires changing this file — just declare
 * `component` on the preset and register it in widgets/index.ts.
 */
export const WidgetNode = memo(function WidgetNode(props: WidgetNodeProps) {
  const preset = WIDGET_ALL_PRESET_MAP.get(String(props.data.presetId ?? ""));
  if (preset?.component) {
    const Comp = preset.component;
    return (
      <ReactWidgetShell {...props}>
        <Comp {...props} />
      </ReactWidgetShell>
    );
  }
  return <WidgetNodeImpl {...props} />;
});

const WidgetNodeImpl = memo(function WidgetNodeImpl({
  id,
  data,
  selected,
  dragging,
  width,
  height,
  positionAbsoluteX,
  positionAbsoluteY,
}: WidgetNodeProps) {
  const canvas = useCanvasApi();
  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();
  const { canEdit } = canvas;
  const { updateNode } = useReactFlow();
  const { isResizingRef } = useCanvasUi();
  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  // Stable ref so action handlers always see the current node width without
  // needing it in the useCallback dep array (avoids stale closure issues).
  const nodeWidthRef = useRef<number | undefined>(width);
  nodeWidthRef.current = width;

  // ── Two-phase CSS-scale resize ────────────────────────────────────────────
  //
  // Phase 1 (measurement): render at fit-content to read intrinsic dimensions.
  // Phase 2 (scaled):      render at node width×height with CSS scale applied.
  //
  // This lets widgets authored at a fixed size (e.g. 360px wide) still fill
  // arbitrary node dimensions cleanly on the canvas, without reflowing their
  // HTML layout. The scale preserves the widget's original design intent.

  const contentRef = useRef<HTMLDivElement>(null);
  const [intrinsicSize, setIntrinsicSize] = useState<{
    w: number;
    h: number;
  } | null>(null);

  useEffect(() => {
    let rafId: number;
    const measure = () => {
      const el = contentRef.current;
      if (!el) return;
      const { offsetWidth, offsetHeight } = el;
      if (offsetWidth > 0 && offsetHeight > 0) {
        setIntrinsicSize({ w: offsetWidth, h: offsetHeight });
      } else {
        // Element not yet laid out — retry next frame
        rafId = requestAnimationFrame(measure);
      }
    };
    rafId = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(rafId);
    // Re-measure when HTML changes
  }, [data.html]);

  // ── Sync node to aspect-correct dimensions on first measurement ──────────
  //
  // Runs once after the intrinsic HTML size is measured and corrects the node
  // dimensions so that scale = 1.0 (single-click) or the content exactly fills
  // the node boundary (drag-to-place).
  //
  // Single-click: no explicit width/height → use intrinsic size directly.
  // Drag-to-place: width AND height are already set from the drag rectangle,
  //   but they may not match the intrinsic aspect ratio, leaving a transparent
  //   gap on one axis. We lock to the intrinsic ratio using the limiting axis.
  const syncedRef = useRef(false);
  useEffect(() => {
    if (!intrinsicSize || syncedRef.current) return;
    syncedRef.current = true;

    let finalW = intrinsicSize.w;
    let finalH = intrinsicSize.h;

    if (width || height) {
      // Drag placement: user drew a rectangle — correct both dims to the
      // intrinsic aspect ratio so the content fills the full node boundary.
      const dragW = width || intrinsicSize.w;
      const dragH = height || intrinsicSize.h;
      const s = Math.min(dragW / intrinsicSize.w, dragH / intrinsicSize.h);
      finalW = Math.round(intrinsicSize.w * s);
      finalH = Math.round(intrinsicSize.h * s);
    }

    updateNode(id, { width: finalW, height: finalH });
    canvas.updateNodeDimensions(id, { width: finalW, height: finalH });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intrinsicSize]);

  // Actual node dimensions from React Flow. Use || (not ??) so React Flow's
  // initial width=0 (before measurement) also falls back to intrinsic size —
  // the same fix applied to EntityNode. For single-click placements with no
  // explicit dimensions, width is undefined and intrinsicSize drives the size.
  const targetW = width || intrinsicSize?.w || 0;
  const targetH = height || intrinsicSize?.h || 0;

  const scaleX = intrinsicSize && targetW > 0 ? targetW / intrinsicSize.w : 1;
  const scaleY = intrinsicSize && targetH > 0 ? targetH / intrinsicSize.h : 1;
  // Uniform scale so the content is never distorted. keepAspectRatio on the
  // NodeResizer ensures both axes always change together, so scaleX === scaleY
  // after the initial placement sync.
  const scale = Math.min(scaleX, scaleY);

  const isScaled = !!intrinsicSize && targetW > 0 && targetH > 0;

  // ── data-action click delegation ─────────────────────────────────────────
  //
  // Widget HTML can include elements with [data-action] + [data-payload].
  // CSS `pointer-events: auto` on those elements punches through the
  // parent's `pointer-events: none` so clicks reach us via bubbling.

  const handleActionClick = useCallback(
    (e: React.MouseEvent) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>(
        "[data-action]",
      );
      if (!target || !canEdit) return;

      e.stopPropagation();

      const action = target.dataset.action;
      let payload: Record<string, string> = {};
      try {
        payload = JSON.parse(target.dataset.payload || "{}");
      } catch {
        /* malformed payload — ignore */
      }

      switch (action) {
        // ── Generic widget actions ──────────────────────────────────────────

        /**
         * widget:transition — replace this widget with another preset at the
         * same canvas position.  Works like onboarding:next but uses
         * WIDGET_ALL_PRESET_MAP, which includes the internal timer-running
         * presets not visible in the picker.
         *
         * payload: { presetId: string }
         */
        case "widget:transition": {
          const { presetId: targetId } = payload;
          if (!targetId) break;
          const preset = WIDGET_ALL_PRESET_MAP.get(String(targetId));
          if (!preset) break;
          const initData = preset.initialData ?? {};
          const html = preset.rebuildHtml
            ? preset.rebuildHtml({ ...initData })
            : preset.html;
          canvas.addNode({
            id: uuidv4(),
            type: "widgetNode",
            position: { x: positionAbsoluteX, y: positionAbsoluteY },
            width: preset.defaultWidth,
            ...(preset.zIndex !== undefined && { zIndex: preset.zIndex }),
            data: {
              html,
              label: preset.label,
              presetId: preset.id,
              ...initData,
            },
          } as Parameters<typeof canvas.addNode>[0]);
          canvas.removeNode(id);
          break;
        }

        default:
          break;
      }
    },
    // Include `data` so action handlers always read fresh state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, canvas, id, positionAbsoluteX, positionAbsoluteY, data],
  );

  // Track resize origin so we can compensate position when a left/top handle
  // is used — identical to ShapeNode's pattern which fixes the choppiness.
  const resizeOriginRef = useRef<{ x: number; y: number } | null>(null);

  return (
    <>
      {/* NodeResizer — aspect-ratio locked using measured intrinsic dimensions.
          keepAspectRatio prevents the transparent-gap problem caused by the
          uniform CSS scale: both axes always change together so the content
          fills the full node bounds at any size.
          isResizingRef gates useCanvasOnNodesChange from writing to Loro during
          live drag (matching EntityNode) — historically prevented a ~50Hz
          oscillation when keepAspectRatio fought RF's snapGrid; RF snapping
          is off now (widgets resize smoothly and settle on release) but the
          write gate stays. */}
      {canEdit && (
        <NodeResizer
          isVisible={!!selected && !dragging}
          minWidth={96}
          minHeight={48}
          keepAspectRatio
          onResizeStart={(_evt, params) => {
            resizeOriginRef.current = { x: params.x, y: params.y };
            isResizingRef.current = true;
          }}
          onResizeEnd={(_evt, params) => {
            isResizingRef.current = false;
            // Snap width to grid. Derive height from the measured intrinsic
            // ratio so handles stay exactly flush with visual content across
            // repeated resizes. Snapping both axes independently accumulates
            // ratio drift that makes the handles and content diverge over time.
            const w = Math.max(96, snapTo(params.width, SNAP_X));
            const h = intrinsicSize
              ? Math.max(
                  48,
                  Math.round(intrinsicSize.h * (w / intrinsicSize.w)),
                )
              : Math.max(48, snapTo(params.height, SNAP_Y));
            const origin = resizeOriginRef.current;

            // When a left/top handle shifts the origin, compensate position so
            // the non-dragged edge stays stable after snap quantisation.
            const x =
              origin && params.x !== origin.x
                ? params.x + params.width - w
                : params.x;
            const y =
              origin && params.y !== origin.y
                ? params.y + params.height - h
                : params.y;

            updateNode(id, { position: { x, y }, width: w, height: h });
            canvas.updateNodeDimensions(id, { width: w, height: h });
            canvas.updateNodePosition(id, { x, y });
          }}
        />
      )}

      {/* Floating toolbar — shown for single selection; suppressed when
          MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2). */}
      <AnimatePresence>
        {selected &&
          selectedNodeCount === 1 &&
          !isMultiSelectToolbarOpen &&
          canEdit &&
          isScaled && (
            <CanvasFloatingToolbarPortal
              positionAbsoluteX={positionAbsoluteX}
              positionAbsoluteY={positionAbsoluteY}
              width={targetW}
              topOffset={56}
            >
              <WidgetFloatingToolbar nodeId={id} nodeData={data} />
            </CanvasFloatingToolbarPortal>
          )}
      </AnimatePresence>

      {/* Outer container — sized to the node's Loro dimensions once known.
          During Phase 1 (measurement) it's fit-content so we can read the
          intrinsic size. During Phase 2 it sizes to the target dimensions.
          overflow:visible so absolutely-positioned decorative elements
          (e.g. aura halos, sparkles) are not clipped by the bounding box. */}
      <div
        className={cn(
          isSectionPreviewMember &&
            "ring-2 ring-canvas-selection/60 rounded-lg",
        )}
        style={
          isScaled
            ? {
                width: targetW,
                height: targetH,
                overflow: "visible",
                position: "relative",
              }
            : {
                width: "fit-content",
                height: "fit-content",
                display: "inline-block",
              }
        }
        onClick={handleActionClick}
      >
        {/* Scale wrapper — transforms content to fill target dimensions */}
        <div
          ref={contentRef}
          style={
            isScaled
              ? {
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  // Preserve intrinsic dimensions so the scale math stays correct
                  width: intrinsicSize!.w,
                  height: intrinsicSize!.h,
                }
              : undefined
          }
        >
          <WidgetContent html={data.html as string} />
        </div>
      </div>
    </>
  );
});
